package net.gorillagroove.discovery

import net.gorillagroove.util.takeIfNotBlank

@Suppress("KotlinRedundantDiagnosticSuppress")
object ImportCsvParser {

    private val requiredFields by lazy {
        setOf(CsvField.NAME, CsvField.ARTIST, CsvField.LENGTH)
    }

    // Theoretically you can use whatever you want to generate a CSV that is appropriate.
    // But it's intended to use this: https://www.spotlistr.com/
    // Valid CSV headers:  Artist(s) Name,Track Name,Album Name,Length,Release Date
    // (Note, as of April 2025, the exporter actually exports the colum as "Arist(s) Name".
    // I allow the typo out of necessity.
    fun parseCsv(text: String): List<MetadataImportRequest> {
        val lines = text.split("\n")
        if (lines.isEmpty()) {
            throw IllegalArgumentException("Invalid CSV provided")
        }

        val header = lines[0].split(",").mapIndexedNotNull { index, headerValue ->
            val field = CsvField.entries.firstOrNull { it.csvName.contains(headerValue) }
                ?: return@mapIndexedNotNull null

            return@mapIndexedNotNull field to index
        }.toMap()

        if (header.keys.intersect(requiredFields).size != requiredFields.size) {
            val requiredFieldsMessage = requiredFields.joinToString { it.csvName.joinToString(" OR ") }
            throw CsvFormatException("CSV is not valid. Needs the ${requiredFields.size} fields: $requiredFieldsMessage")
        }

        return lines.subList(1, lines.size).mapNotNull { trackLine ->
            // The CSV is separated by " , " not just "," presumably to get around commas in names
            val fields = trackLine.split(" , ")

            // Track Name, Artist, and Length are all required fields. If we don't get a valid value, there is
            // no realistic way we will be able to find a matching video to download.
            return@mapNotNull MetadataImportRequest(
                name = header[CsvField.NAME]?.let { fields[it].parseName() } ?: return@mapNotNull null,
                artist = header[CsvField.ARTIST]?.let { fields[it].parseArtist() } ?: return@mapNotNull null,
                album = header[CsvField.ALBUM]?.let { fields[it] } ?: "",
                releaseYear = header[CsvField.RELEASE_DATE]?.let { fields[it].parseYear() } ?: -1,
                trackNumber = -1,
                length = header[CsvField.LENGTH]?.let { fields[it] }?.parseLength() ?: return@mapNotNull null,
            )
        }
    }

    @Suppress("NOTHING_TO_INLINE")
    private inline fun String.parseLength(): Int? {
        val (hours, minutes, seconds) = this.split(":").map { it.toIntOrNull() ?: return null }
        return (hours * 3600) + (minutes * 60) + seconds
    }

    // I see it in three formats: 2001, 1976-07, 2024-03-25
    // In all cases, the year is the first entry. So that makes it easy.
    @Suppress("NOTHING_TO_INLINE")
    private inline fun String.parseYear(): Int? {
        return this.split("-")[0].toIntOrNull()
    }

    // The CSV exporter separates artist names with a semi-colon, which is fine, but GG expects artists to
    // be comma-separated. Easy fix.
    @Suppress("NOTHING_TO_INLINE")
    private inline fun String.parseArtist(): String? {
        return this.replace("; ", ", ").takeIfNotBlank()
    }

    @Suppress("NOTHING_TO_INLINE")
    private inline fun String.parseName(): String? {
        return this.takeIfNotBlank()
    }
}

class CsvFormatException(message: String) : RuntimeException(message)

private enum class CsvField(val csvName: Set<String>) {
    ARTIST(setOf("Arist(s) Name", "Artist(s) Name")),
    NAME(setOf("Track Name")),
    ALBUM(setOf("Album Name")),
    LENGTH(setOf("Length")),
    RELEASE_DATE(setOf("Release Date"))
}
